import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

/**
 * Thin wrapper around `useForm` with project defaults.
 *
 * - `mode: "onBlur"` runs validation when fields blur (first pass)
 * - `reValidateMode: "onChange"` re-validates as the user fixes errors
 * - `shouldFocusError: true` jumps focus to the first invalid field on submit
 * - `shouldUnregister: false` keeps values when fields unmount (critical for
 *   dynamic schemas — the custom-tables row form depends on this)
 *
 * The Zod resolver is memoized on the schema reference so consumers can build
 * the schema dynamically without thrashing the resolver on every render.
 */
const useBaseForm = ({ schema, defaultValues } = {}) => {
  const resolver = useMemo(
    () => (schema ? zodResolver(schema) : undefined),
    [schema]
  );

  return useForm({
    resolver,
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
  });
};

export default useBaseForm;
